import React, { useState, useEffect } from 'react';
import { Smartphone, Download, X, Share2, PlusSquare, CheckCircle, Sparkles } from 'lucide-react';

export const PwaInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [isIos, setIsIos] = useState<boolean>(false);

  useEffect(() => {
    // Detect if running in Standalone / WebView / PWA mode
    const isStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://');

    setIsStandalone(isStandaloneMode);

    // Detect iOS
    const ua = window.navigator.userAgent;
    const isIosDevice = /iphone|ipad|ipod/i.test(ua);
    setIsIos(isIosDevice);

    const bannerTimer = !isStandaloneMode && sessionStorage.getItem('gb_pwa_dismissed') !== 'true'
      ? window.setTimeout(() => setShowBanner(true), 1800)
      : undefined;

    // Handle install prompt event for Android/Chrome
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      if (bannerTimer) window.clearTimeout(bannerTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // Trigger haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(25);
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('gb_pwa_dismissed', 'true');
  };

  if (isStandalone) {
    return null; // App is already installed and running as a standalone app/webview!
  }

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Bottom Floating Web App Install Card (Non-intrusive) */}
      <div className="fixed bottom-16 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-xs z-30 animate-in slide-in-from-bottom duration-300">
        <div className="bg-slate-900/95 backdrop-blur-md border border-orange-500/40 rounded-2xl p-3 shadow-2xl flex items-center justify-between gap-3 text-xs text-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-2 bg-orange-500/20 text-orange-400 rounded-xl shrink-0 border border-orange-500/30">
              <Smartphone className="w-4 h-4" />
            </div>
            <div className="truncate">
              <div className="font-bold truncate text-white text-xs">GigaBalls FC Web App</div>
              <p className="text-[10px] text-slate-400 truncate">Install on Home Screen</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleInstallClick}
              className="bg-orange-500 hover:bg-orange-600 active:scale-95 text-slate-950 font-black px-3 py-1.5 rounded-xl text-xs transition shadow-md flex items-center gap-1 touch-manipulation"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Install</span>
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 text-slate-400 hover:text-white transition touch-manipulation"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS / Webview Setup Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-slate-900 border-2 border-orange-500/40 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 bg-orange-500/20 text-orange-400 rounded-2xl border border-orange-500/30">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-white text-base">Install GigaBalls FC</h3>
                  <p className="text-xs text-slate-400">Add to your Phone Home Screen</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInstructions(false)}
                className="p-2 text-slate-400 hover:text-white rounded-xl bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 bg-slate-950 p-4 rounded-2xl border border-slate-800 text-xs text-slate-300">
              {isIos ? (
                <>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">1</span>
                    <p>Tap the <span className="font-bold text-white inline-flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Share</span> button in Safari browser bar.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">2</span>
                    <p>Scroll down and tap <span className="font-bold text-white inline-flex items-center gap-1"><PlusSquare className="w-3.5 h-3.5" /> Add to Home Screen</span>.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">3</span>
                    <p>Launch <span className="font-bold text-orange-400">GigaBalls FC</span> directly from your app drawer as a standalone web app!</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">1</span>
                    <p>Tap the <b>3 dots menu (⋮)</b> in Chrome or your mobile browser.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">2</span>
                    <p>Select <b>"Install app"</b> or <b>"Add to Home Screen"</b>.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="p-1.5 bg-slate-800 text-orange-400 rounded-lg shrink-0">3</span>
                    <p>Enjoy GigaBalls FC in standalone fullscreen mode!</p>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowInstructions(false)}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-slate-950 font-black rounded-2xl transition shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Got it!</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
